import { useEffect, useMemo, useRef, useState } from 'react';

import { api } from '../../../api';
import type { FeatureContext } from '../../types';
import './riya.css';

interface SpeechRecognitionResultLike {
  0: { transcript: string };
  isFinal?: boolean;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: SpeechRecognitionResultLike[];
}

interface SpeechRecognitionErrorEventLike {
  error: string;
  message?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

function createSilentWavBase64(durationMs: number): string {
  const sampleRate = 8000;
  const numChannels = 1;
  const bitsPerSample = 16;
  const bytesPerSample = bitsPerSample / 8;
  const totalSamples = Math.max(1, Math.floor((sampleRate * durationMs) / 1000));
  const dataSize = totalSamples * numChannels * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);

  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function resolveSpeechRecognition(): SpeechRecognitionConstructor | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const anyWindow = window as typeof window & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };

  return anyWindow.SpeechRecognition ?? anyWindow.webkitSpeechRecognition ?? null;
}

export function RiyaFeature({ isAuthenticated }: FeatureContext) {
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const recognitionTimeoutRef = useRef<number | null>(null);
  const lastTranscriptRef = useRef('');
  const submittedTranscriptRef = useRef(false);
  const listeningGateRef = useRef(true);
  const pendingRecognitionRestartRef = useRef(false);
  const thinkingTimerRef = useRef<number | null>(null);
  const thinkingActiveRef = useRef(false);
  const thinkingLoadingRef = useRef(false);
  const promptInFlightRef = useRef(false);
  const [currentName, setCurrentName] = useState<string | null>(null);
  const [previousName, setPreviousName] = useState<string | null>(null);
  const [nextName, setNextName] = useState<string | null>(null);
  const [sortingState, setSortingState] = useState('not_started');
  const [promptText, setPromptText] = useState('');
  const [transcript, setTranscript] = useState('');
  const [responseText, setResponseText] = useState('');
  const [audioBase64, setAudioBase64] = useState<string | null>(null);
  const [overrideText, setOverrideText] = useState('');
  const [hmmAudio, setHmmAudio] = useState<string[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBeginning, setIsBeginning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioReady, setAudioReady] = useState(false);

  const currentGuestLabel = currentName
    ?? (sortingState === 'closing_statement'
      ? 'All guests have been sorted. Ready for the closing statement.'
      : 'Waiting for the controller to start sorting.');

  const recognitionCtor = useMemo(resolveSpeechRecognition, []);
  const supportsSpeechRecognition = Boolean(recognitionCtor);
  const isSecureContext = typeof window !== 'undefined' ? window.isSecureContext : true;
  const isLocalhost = typeof window !== 'undefined'
    ? ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
    : true;
  const canUseSpeechRecognition = supportsSpeechRecognition && (isSecureContext || isLocalhost);

  useEffect(() => {
    if (isAuthenticated) {
      void preloadThinkingSounds();
    }
    void refreshSortingState();
    return () => {
      recognitionRef.current?.stop();
      micStreamRef.current?.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
      if (recognitionTimeoutRef.current) {
        window.clearTimeout(recognitionTimeoutRef.current);
        recognitionTimeoutRef.current = null;
      }
      audioRef.current = null;
      if (thinkingTimerRef.current) {
        window.clearTimeout(thinkingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!audioBase64) {
      audioRef.current = null;
      setAudioReady(false);
    }
  }, [audioBase64]);

  async function handleSortingHatPrompt(prompt: string) {
    if (promptInFlightRef.current) {
      return;
    }
    promptInFlightRef.current = true;
    setIsLoading(true);
    setError(null);
    setAudioReady(false);
    startThinkingSounds();

    try {
      const result = await api.sortingHatPrompt(prompt);
      setResponseText(result.response);
      setAudioBase64(result.audio_base64 ?? null);
      if (result.audio_base64) {
        stopThinkingSounds();
        await playAudioBase64(result.audio_base64, 'audio/mpeg');
        setAudioReady(true);
      }
      await api.completeSortingHatResponse();
      await refreshSortingState();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reach the Sorting Hat.');
    } finally {
      setIsLoading(false);
      stopThinkingSounds();
      promptInFlightRef.current = false;
    }
  }

  async function requestMicAccess(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      return;
    }

    const currentStream = micStreamRef.current;
    if (currentStream && currentStream.active) {
      return;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    micStreamRef.current = stream;
  }

  async function primeAudioPlayback(): Promise<void> {
    const audio = audioRef.current ?? new Audio();
    audioRef.current = audio;
    const silentBase64 = createSilentWavBase64(200);
    audio.src = `data:audio/wav;base64,${silentBase64}`;
    audio.currentTime = 0;
    try {
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to initialize audio playback.');
    }
  }

  async function playAudioBase64(base64: string, mimeType: string): Promise<void> {
    const audio = audioRef.current ?? new Audio();
    audioRef.current = audio;
    audio.src = `data:${mimeType};base64,${base64}`;
    audio.currentTime = 0;

    await new Promise<void>((resolve, reject) => {
      audio.onended = () => resolve();
      audio.onerror = () => reject(new Error('Unable to play audio response.'));
      audio
        .play()
        .catch((err) => reject(err instanceof Error ? err : new Error(String(err))));
    });
  }

  function scheduleThinkingSound(audioChoices: string[]) {
    if (!thinkingActiveRef.current) {
      return;
    }

    const delay = 800 + Math.random() * 1200;
    thinkingTimerRef.current = window.setTimeout(async () => {
      if (!thinkingActiveRef.current) {
        return;
      }
      try {
        const index = Math.floor(Math.random() * audioChoices.length);
        const clip = audioChoices[index];
        await playAudioBase64(clip, 'audio/mpeg');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to play thinking sound.');
      }
      scheduleThinkingSound(audioChoices);
    }, delay);
  }

  async function preloadThinkingSounds(): Promise<string[]> {
    if (thinkingLoadingRef.current) {
      return hmmAudio;
    }
    thinkingLoadingRef.current = true;
    try {
      const variants = [
        'Hmmmmmm...',
        'Hmmm... hmm...',
        'Hmmm? Hmmmm.',
      ];
      const responses = await Promise.all(
        variants.map((variant) => api.speakSortingHat(variant))
      );
      const audioChoices = responses.map((response) => response.audio_base64);
      setHmmAudio(audioChoices);
      return audioChoices;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load thinking sound.');
      return [];
    } finally {
      thinkingLoadingRef.current = false;
    }
  }

  async function startThinkingSounds() {
    if (thinkingActiveRef.current) {
      return;
    }
    thinkingActiveRef.current = true;

    let audioChoices = hmmAudio;
    if (!audioChoices.length) {
      audioChoices = await preloadThinkingSounds();
    }

    if (audioChoices.length) {
      scheduleThinkingSound(audioChoices);
    }
  }

  function stopThinkingSounds() {
    thinkingActiveRef.current = false;
    if (thinkingTimerRef.current) {
      window.clearTimeout(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
    }
  }

  function startListeningTimeout() {
    if (recognitionTimeoutRef.current) {
      window.clearTimeout(recognitionTimeoutRef.current);
    }
    recognitionTimeoutRef.current = window.setTimeout(() => {
      recognitionRef.current?.stop();
      setIsListening(false);
      setError('Listening timed out. Tap Manual listen or Skip listening.');
    }, 8000);
  }

  async function beginRecognitionSession(options?: { deferResults?: boolean }) {
    if (!supportsSpeechRecognition || !recognitionCtor) {
      setError('Speech recognition is not supported in this browser.');
      return;
    }

    setError(null);
    setResponseText('');
    setAudioBase64(null);
    setAudioReady(false);
    setTranscript('');
    lastTranscriptRef.current = '';
    submittedTranscriptRef.current = false;

    const recognition = recognitionRef.current ?? new recognitionCtor();
    recognitionRef.current = recognition;
    recognition.lang = 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    const shouldDefer = options?.deferResults ?? false;
    listeningGateRef.current = !shouldDefer;

    recognition.onresult = (event) => {
      if (!listeningGateRef.current) {
        return;
      }
      let latestTranscript = '';
      let sawFinal = false;
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const chunk = result?.[0]?.transcript?.trim() ?? '';
        if (chunk) {
          latestTranscript = latestTranscript ? `${latestTranscript} ${chunk}` : chunk;
        }
        if (result?.isFinal) {
          sawFinal = true;
        }
      }

      if (latestTranscript) {
        lastTranscriptRef.current = latestTranscript;
        setTranscript(latestTranscript);
      }

      if (sawFinal && !submittedTranscriptRef.current) {
        submittedTranscriptRef.current = true;
        recognitionRef.current?.stop();
        stopThinkingSounds();
        void handleSortingHatPrompt(latestTranscript || lastTranscriptRef.current);
      }
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === 'network') {
        setError(
          'Speech recognition failed due to a network error. Ensure you are on HTTPS (or localhost) and have network access.'
        );
        return;
      }
      if (event.error === 'aborted') {
        setError('Listening was stopped. You can skip listening to continue.');
        return;
      }
      const message = event.message ? ` ${event.message}` : '';
      setError(`Speech recognition failed: ${event.error}.${message}`);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (!listeningGateRef.current) {
        pendingRecognitionRestartRef.current = true;
        return;
      }
      if (recognitionTimeoutRef.current) {
        window.clearTimeout(recognitionTimeoutRef.current);
        recognitionTimeoutRef.current = null;
      }
      const fallbackText = lastTranscriptRef.current.trim();
      if (fallbackText && !submittedTranscriptRef.current) {
        submittedTranscriptRef.current = true;
        stopThinkingSounds();
        void handleSortingHatPrompt(fallbackText);
      } else if (!submittedTranscriptRef.current) {
        setError('No speech was detected. Please try again.');
      }
    };

    try {
      setIsListening(true);
      if (listeningGateRef.current) {
        startListeningTimeout();
      }
      recognition.start();
    } catch (err) {
      setIsListening(false);
      if (recognitionTimeoutRef.current) {
        window.clearTimeout(recognitionTimeoutRef.current);
        recognitionTimeoutRef.current = null;
      }
      setError(err instanceof Error ? err.message : 'Unable to start speech recognition.');
    }

    if (shouldDefer) {
      return async () => {
        if (listeningGateRef.current) {
          return;
        }
        listeningGateRef.current = true;
        if (pendingRecognitionRestartRef.current) {
          pendingRecognitionRestartRef.current = false;
          await beginRecognitionSession();
        } else {
          startListeningTimeout();
        }
      };
    }
    return null;
  }

  async function handleStartListening() {
    if (!canUseSpeechRecognition || !recognitionCtor) {
      setError(
        'Speech recognition requires HTTPS (or localhost) and a supported browser.'
      );
      return;
    }

    await primeAudioPlayback();

    try {
      await requestMicAccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone access is required.');
      return;
    }

    if (!isAuthenticated) {
      setError('Sign in to speak with the Sorting Hat.');
      return;
    }

    await beginRecognitionSession();
  }

  function handleStopListening() {
    recognitionRef.current?.stop();
    setIsListening(false);
  }

  function handleSkipListening() {
    recognitionRef.current?.stop();
    setIsListening(false);
    setTranscript('');
    void handleSortingHatPrompt('');
  }

  async function handleBeginSorting() {
    if (!canUseSpeechRecognition || !recognitionCtor) {
      setError(
        'Speech recognition requires HTTPS (or localhost) and a supported browser.'
      );
      return;
    }

    if (!isAuthenticated) {
      setError('Sign in to speak with the Sorting Hat.');
      return;
    }

    setIsBeginning(true);
    setError(null);
    setPromptText('');
    setCurrentName(null);
    setPreviousName(null);
    setNextName(null);
    if (sortingState === 'not_started') {
      setSortingState('opening_speech');
    }

    await primeAudioPlayback();

    try {
      await requestMicAccess();
      const stepResult = await api.stepSortingHat();
      setPromptText(stepResult.prompt_text ?? '');
      let audioError: Error | null = null;
      let openListeningGate: (() => void) | null = null;
      if (stepResult.listen) {
        openListeningGate = await beginRecognitionSession({ deferResults: true });
      }
      try {
        await playAudioBase64(stepResult.audio_base64, 'audio/mpeg');
      } catch (err) {
        audioError = err instanceof Error ? err : new Error('Unable to play audio prompt.');
      }

      if (stepResult.next_state) {
        await api.advanceSortingHatState(stepResult.next_state);
      }

      const nextState = await refreshSortingState();
      const shouldListen = Boolean(stepResult.listen);

      if (shouldListen && openListeningGate) {
        openListeningGate();
      }
      if (audioError) {
        setError(audioError.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to begin sorting.');
    } finally {
      setIsBeginning(false);
    }
  }

  async function refreshSortingState(): Promise<string> {
    try {
      const state = await api.getSortingHatState();
      if (!state.order.length) {
        setPreviousName(null);
        setCurrentName(null);
        setNextName(null);
        setSortingState(state.state);
        return state.state;
      }

      const index = state.current_index;
      setPreviousName(index > 0 ? state.order[index - 1] : null);
      setCurrentName(state.order[index] ?? null);
      setNextName(index + 1 < state.order.length ? state.order[index + 1] : null);
      setSortingState(state.state);
      return state.state;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to refresh sorting state.');
      return sortingState;
    }
  }

  async function handleSpeakOverride() {
    const trimmed = overrideText.trim();
    if (!trimmed) {
      setError('Provide text to speak.');
      return;
    }

    setError(null);
    try {
      const response = await api.speakSortingHat(trimmed);
      await playAudioBase64(response.audio_base64, 'audio/mpeg');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to speak override text.');
    }
  }

  async function handleReplayNameAnnouncement() {
    setError(null);
    try {
      const response = await api.replaySortingHatName();
      await playAudioBase64(response.audio_base64, 'audio/mpeg');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to replay the name announcement.');
    }
  }

  function handlePlayResponse() {
    const audio = audioRef.current;
    if (!audio) {
      setError('Audio is not ready yet.');
      return;
    }

    audio.play().catch((err) => {
      setError(err instanceof Error ? err.message : 'Unable to play audio response.');
    });
  }

  const statusLabel = (() => {
    if (isBeginning) {
      return 'Preparing the Sorting Hat...';
    }
    if (isListening) {
      return 'Listening...';
    }
    if (isLoading) {
      return 'Sorting Hat is thinking...';
    }
    if (!canUseSpeechRecognition) {
      return 'Speech recognition requires HTTPS or localhost.';
    }
    if (sortingState === 'not_started') {
      return 'Tap start sorting to begin the ceremony.';
    }
    if (sortingState === 'opening_speech') {
      return 'Opening speech in progress...';
    }
    if (sortingState === 'wait_to_announce_guest') {
      return 'Tap start to call the next guest.';
    }
    if (sortingState === 'wait_to_ask_guest') {
      return 'Tap ask guest to begin the question.';
    }
    if (sortingState === 'closing_statement') {
      return 'Tap play closing statement to finish.';
    }
    return 'Tap the main button to continue.';
  })();

  return (
    <section className="page-section riya">
      <div className="riya-card">
        <div className="riya-card__header">
          <div>
            <h2>Sorting Hat</h2>
            <p>
              Follow the Sorting Hat ceremony. It will call a guest, ask their preference, and then announce the house.
            </p>
          </div>
          <div className="riya-card__actions">
            <button
              type="button"
              className="riya-mic-button riya-action-primary"
              onClick={handleBeginSorting}
              disabled={!canUseSpeechRecognition || isListening || isLoading || isBeginning}
            >
              {sortingState === 'not_started' && 'Start sorting'}
              {sortingState === 'opening_speech' && 'Opening speech'}
              {sortingState === 'wait_to_announce_guest' && `Start ${currentName ?? 'guest'}`}
              {sortingState === 'wait_to_ask_guest' && 'Ask guest'}
              {sortingState === 'closing_statement' && 'Play closing statement'}
            </button>
            <button
              type="button"
              className={`riya-mic-button${isListening ? ' riya-mic-button--listening' : ''}`}
              onClick={handleStartListening}
              disabled={!canUseSpeechRecognition || isListening || isLoading || isBeginning}
              aria-pressed={isListening}
            >
              {isListening ? 'Listening...' : 'Manual listen'}
            </button>
            <button
              type="button"
              className="riya-stop-button"
              onClick={handleStopListening}
              disabled={!isListening}
            >
              Stop listening
            </button>
            <button
              type="button"
              className="riya-skip-button"
              onClick={handleSkipListening}
              disabled={isLoading || isBeginning}
            >
              Skip listening
            </button>
            {sortingState === 'wait_to_ask_guest' && currentName ? (
              <button
                type="button"
                className="riya-skip-button"
                onClick={handleReplayNameAnnouncement}
                disabled={isListening || isLoading || isBeginning}
              >
                Repeat name
              </button>
            ) : null}
          </div>
        </div>

        <div className="riya-status" role="status" aria-live="polite">
          {statusLabel}
        </div>

        <div className="riya-grid">
          <div className="riya-panel">
            <h3>Current guest</h3>
            <div className="riya-guest-list">
              <p className={previousName ? 'riya-panel__text' : 'riya-panel__placeholder'}>
                Previous: {previousName ?? '—'}
              </p>
              <p className={currentName ? 'riya-panel__text' : 'riya-panel__placeholder'}>
                Current: {currentGuestLabel}
              </p>
              <p className={nextName ? 'riya-panel__text' : 'riya-panel__placeholder'}>
                Next: {nextName ?? '—'}
              </p>
            </div>
            {promptText && (
              <p className="riya-panel__note">{promptText}</p>
            )}
            {transcript && (
              <p className="riya-panel__note">Heard: "{transcript}"</p>
            )}
          </div>
          <div className="riya-panel">
            <h3>Sorting Hat response</h3>
            <p className={responseText ? 'riya-panel__text' : 'riya-panel__placeholder'}>
              {responseText || 'The Sorting Hat response will appear here.'}
            </p>
            <button
              type="button"
              className="riya-play-button"
              onClick={handlePlayResponse}
              disabled={!audioReady}
            >
              Play response
            </button>
          </div>
        </div>

        <div className="riya-override">
          <div>
            <h3>Override Sorting Hat</h3>
            <p>Type a custom line to be spoken in the Sorting Hat voice.</p>
          </div>
          <div className="riya-override__controls">
            <input
              type="text"
              value={overrideText}
              onChange={(event) => setOverrideText(event.target.value)}
              placeholder="Enter text to speak..."
            />
            <button
              type="button"
              className="riya-play-button"
              onClick={handleSpeakOverride}
            >
              Speak
            </button>
          </div>
        </div>

        {error && (
          <p className="riya-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
